/**
 * Carrier Passport — authenticated app routes (Vite SPA).
 * Use these paths in React Router and sidebar/nav so links stay consistent.
 * API paths remain in passportConstants.ts + backend passportConstants.ts; no Firestore rule changes for nav.
 */

export const PASSPORT_APP_PATHS = {
  DASHBOARD: '/dashboard',
  /** Master Phase 4 slice 8 — public MC/DOT/name lookup and officer claim flow (no login). */
  ENFORCEMENT_ENTRY: '/enforcement-entry',
  ENFORCEMENT: '/enforcement',
  ACCOUNT: '/account',
  SETTINGS: '/settings',
  ONBOARDING: '/onboarding',
  /** Broker/shipper: partnered carriers → commercial passport drill-in. */
  CONNECTIONS: '/connections',
  /** Broker/shipper: list of Passport access requests you sent to carriers. */
  OUTGOING_CONNECTION_REQUESTS: '/connection-requests/outgoing',
  /** Carrier: pending Passport access requests from brokers/shippers. */
  ACCESS_REQUESTS: '/access-requests',
  /** Carrier: issuer attestation / document submissions inbox (Slice 6). */
  ISSUER_SUBMISSIONS: '/issuer-submissions',
  /** Phase 6 — carrier: compose POST /api/passport/document-packages (register before `/document-packages/:packageId`). */
  DOCUMENT_PACKAGE_SEND: '/document-packages/send',
  /** Phase 6 — carrier: poll GET status (email deep link when broker notify is enabled). */
  DOCUMENT_PACKAGE_DETAIL: (packageId: string) => `/document-packages/${encodeURIComponent(packageId)}`,
  /** CMV — Critical Maintenance Verification full sheet (carrier owner). Phase 2 route. */
  CMV: '/cmv',
  /** CMV — daily quick inspection (carrier owner). Phase 2 route. */
  CMV_DAILY: '/cmv/daily',
  /** Authenticated commercial view (requires partnership or accepted request). */
  CARRIER_PASSPORT: (carrierUserId: string) => `/carrier-passport/${encodeURIComponent(carrierUserId)}`,
  /**
   * Outside-network FMCSA preview — same commercial shell, `viewMode=fmcsa_unregistered`.
   * Register this route before `/carrier-passport/:carrierUserId` so `fmcsa` is not treated as a uid.
   */
  CARRIER_PASSPORT_FMCSA: '/carrier-passport/fmcsa'
} as const;

/** B2B integration partner developer console (Phase 3 slice 7) — API key in sessionStorage; not Firebase carrier login. */
export const PASSPORT_APP_INTEGRATION_PARTNER_PATHS = {
  DASHBOARD: '/integration-partner'
} as const;

/** Third-party / issuer document submitter (Slice 3 SPA). */
/** Fleet org administrator (Phase 2+). */
export const PASSPORT_APP_FLEET_PATHS = {
  HOME: '/fleet',
  REGISTER: '/fleet/register',
  ACCEPT_DRIVER_INVITE: '/fleet/accept-invite',
  ENTER_PIN: '/fleet/enter-pin',
  DRIVER_SUMMARY: (driverUserId: string) =>
    `/fleet/drivers/${encodeURIComponent(driverUserId)}/summary`,
  LOGIN: '/login'
} as const;

export const PASSPORT_NAV_FLEET_LABELS = {
  fleetHome: 'Fleet home',
  fleetOverview: 'Overview',
  fleetDirectory: 'Driver directory',
  account: 'Account',
  inviteDriver: 'Invite driver',
  pendingInvites: 'Pending invitations',
  pendingPinDrivers: 'Pending PIN',
  activeDrivers: 'Active drivers',
  inactiveDrivers: 'Inactive drivers',
  viewComplianceSummary: 'View compliance summary',
  resendInvite: 'Resend invitation',
  revokeInvite: 'Revoke invitation',
  inactivateDriver: 'Inactivate',
  reactivateDriver: 'Reactivate',
  removeDriver: 'Remove driver',
  editDriverProfile: 'Save driver profile',
  enterActivationPin: 'Enter activation PIN',
  sendInvitation: 'Send invitation'
} as const;

export const PASSPORT_APP_ISSUER_PATHS = {
  HUB: '/issuer',
  SUBMIT: '/issuer/submit',
  /** Slice 4: FMCSA lookup + confirm + upload (invite token in query or session). */
  LOOKUP: '/issuer/lookup',
  /** Submit Documents Phase 1 — public carrier lookup + request access (Turnstile; no invite). */
  REQUEST_ACCESS: '/issuer/request-access',
  REGISTER: '/issuer/register',
  LOGIN: '/issuer/login',
  HOME: '/issuer/home',
  PROFILE: '/issuer/profile'
} as const;

export type PassportAppPathKey = keyof typeof PASSPORT_APP_PATHS;

/** Sidebar + primary IA labels (display strings). */
export const PASSPORT_NAV_LABELS = {
  passport: 'Passport',
  dashboardTransport: 'Dashboard',
  enforcement: 'Enforcement',
  account: 'Account',
  /** Broker/shipper organization profile route (same path as Account; label only). */
  organization: 'Organization',
  logout: 'Logout',
  settings: 'Settings',
  connections: 'Connections / Partners',
  outgoingRequests: 'Outgoing requests',
  accessRequests: 'Access requests',
  issuerSubmissions: 'Issuer submissions',
  /** Phase 6 — carrier document package to connected broker/shipper. */
  documentPackageSend: 'Send document package',
  /** CMV — sidebar label (full page title in PASSPORT_CMV_UI.pageTitle). */
  maintenance: 'Maintenance',
} as const;

/** Parent platform — public marketing / app entry (Tranzit main site). */
export const PASSPORT_PUBLIC_TRANZIT_APP_URL = 'https://tranzit.io' as const;
